import React, { useContext, useEffect, useState } from "react";
import {
  createStyles,
  Navbar,
  Group,
  Center,
  Box,
  UnstyledButton,
  ScrollArea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { APP_ROUTES, NavigationLinkProps } from "../../providers/Router";
import UserCard from "../elements/UserCard";
import UserAccessControlUtils from "../../utils/UserAccessControlUtils";
import { AuthContext } from "../../providers/AuthProvider";
import { queryClient } from "../../App";
import { useEnabledFeatureFlag as isEnabledFeatureFlag } from "../../common/feature-flagging/FeatureFlagProvider";
import NavLink from "./NavLink";
import JemLogo from "../../common/assets/logo.webp";

const useStyles = createStyles((theme, _params, getRef) => {
  const icon = getRef("icon");
  return {
    navbar: {
      backgroundColor: "var(--pink-50)",
      borderRight: "none",
    },

    title: {
      letterSpacing: -0.25,
    },

    link: {
      ...theme.fn.focusStyles(),
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      fontSize: theme.fontSizes.sm,
      color: "var(--greyscale-text-caption)",
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      fontWeight: 500,

      "&:hover": {
        backgroundColor: "var(--pink-100)",
        color: "var(--greyscale-text-title)",

        [`& .${icon}`]: {
          color: "var(--greyscale-text-title)",
        },
      },
    },

    linkIcon: {
      ref: icon,
      color: "var(--greyscale-text-caption)",
      marginRight: theme.spacing.xs,
      flexShrink: 0,
    },

    linkActive: {
      "&&, &&:hover": {
        backgroundColor: "var(--pink-100)",
        color: "var(--greyscale-text-title)",
        [`& .${icon}`]: {
          color: "var(--greyscale-text-title)",
        },
      },
    },

    footer: {
      paddingTop: theme.spacing.md,
      marginTop: theme.spacing.md,
      borderTop: `1px solid var(--pink-200)`,
    },

    linkActionIcon: {
      width: 50,
      height: 50,
      borderRadius: theme.radius.md,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": {
        backgroundColor: "var(--pink-100)",
        color: "var(--greyscale-text-title)",
      },
    },
    activeActionIcon: {
      "&, &:hover": {
        backgroundColor: "var(--pink-100)",
        color: "var(--greyscale-text-title)",
      },
    },

    sidebarToggle: {
      ...theme.fn.focusStyles(),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: theme.radius.sm,
      color: "var(--pink-900)",
    },

    brand: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    },
  };
});

function getWidth(opened: boolean): number {
  if (opened) {
    return 232;
  }
  return 80;
}

const SideNavbar: React.FC = () => {
  const shouldExpand = useMediaQuery("(min-width: 768px)");
  const [expanded, setExpanded] = useState<boolean>(true);

  useEffect(() => {
    setExpanded(shouldExpand);
  }, [shouldExpand]);

  const location = useLocation();
  const { classes, cx } = useStyles();
  const auth = useContext(AuthContext);

  const [currentPath, setCurrentPath] = useState(location.pathname);

  useEffect(() => {
    queryClient.refetchQueries(["AuthUserProfileApiService"]);
  }, [currentPath]);

  if (!auth) {
    return <Navigate to="/" replace />;
  }
  /**
   * Get the navigation links that the user can access based on features and permissions
   * @returns
   */
  const getProtectedNavigationLinks = (): NavigationLinkProps[] => {
    if (auth) {
      return APP_ROUTES.filter((route) => {
        if (route.isFeatureFlagged && route.requiredFeatureFlag) {
          const hasFeatureEnabled = isEnabledFeatureFlag(
            route.requiredFeatureFlag
          );

          return hasFeatureEnabled;
        }
        return true;
      })
        .filter((route) => {
          const result = UserAccessControlUtils.hasFeature(
            auth.user_features,
            route.requiredFeature
          );
          return result;
        })
        .filter((route) => {
          const result = UserAccessControlUtils.hasPermission(
            auth.permissions,
            route.requiredPermissions
          );
          return result;
        })
        .filter((route) => {
          if (!route.requiredCapability) return true;
          return UserAccessControlUtils.hasCapability(
            auth.ta_capabilities,
            route.requiredCapability
          );
        })
        .map((route) => ({
          ...route,
          path: route.path.replace(/\/:.*?\?/g, ""), // Remove optional params
          children: route.children?.filter((child) => {
            if (child.isFeatureFlagged && child.requiredFeatureFlag) {
              return isEnabledFeatureFlag(child.requiredFeatureFlag);
            }
            return true;
          }),
        }));
    }
    return [];
  };

  const links = getProtectedNavigationLinks()
    .filter((it) => it.sidebar)
    .map((it) => (
      <NavLink
        key={it.id}
        link={it}
        setCurrentPath={setCurrentPath}
        classes={classes}
        cx={cx}
        expanded={expanded}
        showIcon
      />
    ));

  const linksSecondary = getProtectedNavigationLinks()
    .filter((it) => it.secondarySidebar)
    .map((it) => (
      <NavLink
        key={it.id}
        link={it}
        setCurrentPath={setCurrentPath}
        classes={classes}
        cx={cx}
        expanded={expanded}
        showIcon
      />
    ));

  const brand = (
    <Group spacing="sm" className={classes.brand}>
      {expanded && (
        <a href="/" title="Go to Homepage" target="_blank" rel="noreferrer">
          <img src={JemLogo} style={{ width: "64px" }} alt="Jem Logo" />
        </a>
      )}
      <UnstyledButton
        className={classes.sidebarToggle}
        onClick={() => setExpanded((e) => !e)}
        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        {expanded ? (
          <ChevronLeft size={18} aria-hidden="true" focusable={false} />
        ) : (
          <ChevronRight size={18} aria-hidden="true" focusable={false} />
        )}
      </UnstyledButton>
    </Group>
  );
  return (
    <Navbar
      hiddenBreakpoint="sm"
      p="md"
      className={classes.navbar}
      hidden
      width={{ base: getWidth(expanded) }}
    >
      {/* First section with normal height (depends on section content) */}
      <Navbar.Section
        style={{
          borderBottom: `1px solid var(--pink-200)`,
          paddingTop: "var(--spacing-xxxs)",
          paddingBottom: "var(--spacing-xs)",
        }}
      >
        {expanded ? brand : <Center>{brand}</Center>}
      </Navbar.Section>

      {/* Grow section scrolls when content exceeds available height */}
      <Navbar.Section
        grow
        component={ScrollArea}
        mt={20}
        scrollbarSize={6}
        styles={{
          viewport: {
            overflowX: "hidden",
            "& > div": {
              display: "block !important",
              width: "100%",
            },
          },
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            borderBottom: `1px solid var(--pink-200)`,
            paddingBottom: "20px",
          }}
        >
          {expanded ? (
            links
          ) : (
            <Group direction="column" align="center" spacing={0}>
              {links}
            </Group>
          )}
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: 20,
          }}
        >
          {expanded ? (
            linksSecondary
          ) : (
            <Group direction="column" align="center" spacing={0}>
              {linksSecondary}
            </Group>
          )}
        </Box>
      </Navbar.Section>

      {/* Last section with normal height (depends on section content) */}
      <Navbar.Section className={classes.footer}>
        <UserCard auth={auth} displayText={expanded} />
      </Navbar.Section>
    </Navbar>
  );
};

export default SideNavbar;
